import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { InvoiceTemplate } from '~/api/invoiceTemplates/types';

import { useCreateInvoiceTemplate, usePreviewInvoiceTemplate, useUpdateInvoiceTemplate } from '~/api/invoiceTemplates';
import { AlertDialog, Blocker, Container, Header, Separator } from '~/components';
import { Form } from '~/components/Form';
import { bypassBlocker } from '~/constants/router';
import { CopyrightSection, FormLines, LinesTotal } from '~/features/documents';
import { useDialog } from '~/hooks/useDialog';
import { InvoicePreviewDialog } from '~/pages/InvoicesCreditnotes/components/InvoicePreviewDialog';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import type { InvoiceTemplateForm } from './CreateEditInvoiceTemplateForm.types';

import { linesSectionStyle, sectionsStyle } from './CreateEditInvoiceTemplateForm.css';
import {
  useCalculationData,
  useInvoiceTemplateDefaultValues,
  useInvoiceTemplatePersistedFormValues,
} from './CreateEditInvoiceTemplateForm.hooks';
import { mapInvoiceTemplateFormDataToPayload } from './CreateEditInvoiceTemplateForm.utils';
import { InvoiceTemplateBasicSection } from './sections/InvoiceTemplateBasicSection';
import { UnpauseInvoiceTemplateDialog } from './UnpauseInvoiceTemplateDialog';

type Props =
  | {
      invoiceTemplate: InvoiceTemplate;
      mode: 'edit';
    }
  | {
      invoiceTemplate: null;
      mode: 'create';
    };

export const CreateEditInvoiceTemplateForm = ({ invoiceTemplate, mode }: Props) => {
  const createTemplateMutation = useCreateInvoiceTemplate();
  const updateTemplateMutation = useUpdateInvoiceTemplate(invoiceTemplate?.id ?? 0);
  const previewMutation = usePreviewInvoiceTemplate();

  const defaultValues = useInvoiceTemplateDefaultValues(invoiceTemplate);
  const form = useForm<InvoiceTemplateForm>({ defaultValues });
  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
  } = form;

  useInvoiceTemplatePersistedFormValues(form);

  const unpauseDialog = useDialog<Date, { unpause: boolean } | null>();
  const { calculationData, isFetching: isCalculationDataFetching } = useCalculationData(invoiceTemplate, control);

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'invoices', 'invoiceTemplates', 'validation']);

  const onPreview = () =>
    previewMutation.mutate(mapInvoiceTemplateFormDataToPayload(getValues()), {
      onError: () => toast.danger({ description: t('common:error') }),
    });

  const onSubmit = handleSubmit(async (data) => {
    const canBeUnpaused = mode === 'edit' && invoiceTemplate.intervalStatus === 'Paused' && data.interval !== null;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const result = canBeUnpaused && (await unpauseDialog.open(data.nextInvoiceAt!));

    if (result === null) return;

    const { mutate } = mode === 'create' ? createTemplateMutation : updateTemplateMutation;

    mutate(
      {
        ...mapInvoiceTemplateFormDataToPayload(data),
        ...result,
      },
      {
        onSuccess: ({ id }) => {
          toast.success({
            description: t(mode === 'create' ? 'invoiceTemplates:alerts.successCreated' : 'invoiceTemplates:alerts.successUpdated'),
          });
          navigate(r(routes.showInvoiceTemplate, { invoiceTemplateId: id }), { state: bypassBlocker, replace: true });
        },
        onError: () => toast.danger({ description: t('common:error') }),
      },
    );
  });

  return (
    <>
      <Header.Root>
        <Header.Content
          backTo={mode === 'create' ? routes.invoiceTemplates : r(routes.showInvoiceTemplate, { invoiceTemplateId: invoiceTemplate.id })}
          title={t(`invoiceTemplates:createEdit.header.title.${mode}`)}
        />

        <Header.Actions>
          <Header.Action emphasis="secondary" icon="Visibility" loading={previewMutation.isPending} onClick={onPreview}>
            {t('invoiceTemplates:createEdit.preview')}
          </Header.Action>

          <Header.Action icon="Save" loading={createTemplateMutation.isPending || updateTemplateMutation.isPending} onClick={onSubmit}>
            {t('common:actions.save')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Blocker isBlocked={isDirty} message={t(`invoiceTemplates:createEdit.blocker.${mode}`)} />

      <Container>
        <Form autoComplete="off" onSubmit={onSubmit}>
          <div className={sectionsStyle}>
            {/* Basic info */}
            <InvoiceTemplateBasicSection form={form} />

            <Separator />

            {/* Royalties */}
            <CopyrightSection form={form} required={false} />

            <Separator />

            {/* Invoice lines */}
            <div className={linesSectionStyle}>
              <FormLines disableEntryLines form={form} i18nNamespace="invoices" required={false} />
              <LinesTotal calculationData={calculationData} form={form} isLoading={isCalculationDataFetching} />
            </div>
          </div>
        </Form>
      </Container>

      {/* Preview dialog */}
      <InvoicePreviewDialog html={previewMutation.data} onClose={() => previewMutation.reset()} title={getValues('title') || null} />

      {/* Unpause dialog */}
      <AlertDialog.Root onOpenChange={() => unpauseDialog.close(null)} open={unpauseDialog.isOpen}>
        {unpauseDialog.isOpen && <UnpauseInvoiceTemplateDialog nextInvoiceAt={unpauseDialog.data} onClose={unpauseDialog.close} />}
      </AlertDialog.Root>
    </>
  );
};
